import { Participant as ChatParticipant } from "@twilio/conversations";
import { Mutex } from "async-mutex";
import { identity, mapValues, pipe } from "remeda";
import { OpenMapOptions, Client as SyncClient, SyncMap } from "twilio-sync";
import { logger } from "../datadog/logger";
import type {
  RemoteParticipantEventArgsMap,
  RemoteParticipantMutexEventHandler,
  RemoteParticipantMutexEventMap,
} from "../video-sdk";
import { parseIdentity } from "../video-sdk";
import { MAX_ITEMS_PER_MAP_PAGE, MAX_MAP_PAGE_COUNT } from "./config";
import { MessagesByConversationSid } from "./state";

export async function connectSyncMap(
  client: SyncClient,
  mapOptions: OpenMapOptions,
  onError?: () => void,
) {
  const { id: mapSid } = mapOptions;
  try {
    // Times out if we don't connect to the map within 30 seconds
    const map = await client.map(mapOptions);
    logger.info("Connected to sync map", { mapSid });
    return map;
  } catch (error) {
    onError?.();
    logger.error("Error connecting to sync map", { mapSid }, error as Error);
  }
}

export async function fetchMapItems<T>(map: SyncMap): Promise<T[]> {
  let hasPage = true;
  let pageCount = 0;
  const result = [];
  while (hasPage && pageCount++ < MAX_MAP_PAGE_COUNT) {
    const { items, hasNextPage } = await map.getItems({
      pageSize: MAX_ITEMS_PER_MAP_PAGE,
    });
    hasPage = hasNextPage;
    result.push(...(items as T[]));
  }
  return result;
}

export function chatParticipantToName({ identity }: ChatParticipant) {
  return identity ? parseIdentity(identity).name : "";
}

export function getChatParticipants(
  participantIds: Set<string>,
  chatParticipants: Record<string, ChatParticipant>,
  twilioIdentity?: string,
) {
  return pipe(
    participantIds,
    (idSet) => Array.from(idSet),
    (ids) => ids.filter((id) => id !== twilioIdentity),
    (ids) => ids.map((id) => chatParticipants[id]),
    (participants) => participants.filter(identity),
    (participants) => new Set(participants),
  );
}

export function getMostRecentMessageInConversation(
  messagesByConversationSid: MessagesByConversationSid,
  conversationSid: string,
) {
  const messages = messagesByConversationSid[conversationSid];
  if (!messages) return;
  return messages[messages.length - 1];
}

/**
 * Wraps the given track event handlers in the given mutex, ensuring that only
 * one handler can run at a time. Guaranteeing that global state pertaining to
 * tracks is not modified concurrently when handling track events.
 *
 * This function also ensures that any errors thrown by the handlers are caught
 * and logged.
 */
export function gracefullyHandleMediaEvents(
  mutex: Mutex,
  handlerMapping: RemoteParticipantMutexEventMap,
) {
  return mapValues(
    handlerMapping,
    <K extends keyof RemoteParticipantMutexEventMap>(
      handler: RemoteParticipantMutexEventHandler<K>,
      event: string,
    ) =>
      async (...args: RemoteParticipantEventArgsMap[K]) => {
        const release = await mutex.acquire();
        try {
          await handler(...args);
        } catch (error) {
          const [remoteMediaDelegator] = args;

          logger.error(
            `There was an error when handling a remote media event: ${event}`,
            {
              mediaKind: remoteMediaDelegator.kind,
              mediaSource:
                "source" in remoteMediaDelegator
                  ? remoteMediaDelegator.source
                  : undefined,
            },
            error as Error,
          );
        } finally {
          release();
        }
      },
  );
}
